// src/InterviewViewerPage.js
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  CircularProgress,
  Avatar,
  Chip,
  Divider,
  Stack,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import theme from "./theme";
import NavBar from "./components/NavBar";
import { getToken, isAdmin } from "./auth/token";
import BACKEND_URL from "./config";

import CustomerStorySection from "./components/CustomerStorySection";

// Qualitative satisfaction labels (for overall + JTBD)
import { describe } from "./utils/scoring";

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatSeconds(seconds) {
  if (seconds == null || isNaN(seconds)) return "";
  // Infinity is not NaN, so it slipped past the guard above and rendered as
  // "Infinity:NaN" (Math.floor(Infinity/60) stringifies, Infinity % 60 is NaN).
  // A live-recorded WebM reports an infinite duration until it is recovered.
  if (!Number.isFinite(seconds)) return "--:--";
  const s = Math.floor(seconds);
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  const mm = String(mins).padStart(2, "0");
  const ss = String(secs).padStart(2, "0");
  return `${mm}:${ss}`;
}

function formatDateOnly(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString();
  } catch {
    return String(value);
  }
}

function normalizeOptionalValue(val) {
  if (val === null || val === undefined) return "—";
  const s = String(val).trim();
  if (!s) return "—";
  if (s.toLowerCase() === "unknown") return "—";
  return s;
}

// The landing page stores the payment method as an enum; show the label the
// participant actually picked rather than the raw key.
const PAYMENT_METHOD_LABELS = {
  paypal: "PayPal",
  venmo: "Venmo",
  amazon_gift_card: "Amazon gift card",
  visa_gift_card: "Visa gift card",
};

function paymentMethodLabel(method) {
  const m = normalizeOptionalValue(method);
  if (m === "—") return "—";
  return PAYMENT_METHOD_LABELS[m] || m;
}

// Venmo accepts a phone number, and the gift-card options are a delivery
// address rather than an account, so this field is not always "an email".
const PAYMENT_CONTACT_LABELS = {
  paypal: "payment_email (PayPal email)",
  venmo: "payment_email (Venmo email or phone)",
  amazon_gift_card: "payment_email (gift card delivery)",
  visa_gift_card: "payment_email (gift card delivery)",
};

function paymentContactLabel(method) {
  return PAYMENT_CONTACT_LABELS[String(method || "").trim()] || "payment_email";
}

function isEmptyish(val) {
  return normalizeOptionalValue(val) === "—";
}

function originLabel(origin) {
  if (origin === "self_upload") return "Self-upload";
  if (origin === "ai_interview") return "AI interview";
  return normalizeOptionalValue(origin);
}

// 5 buckets → 5 colors (aligns with 5 qualitative labels in scoring.js)
function scoreColorKey5(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return "text.secondary";
  if (n <= 2) return "error.main";
  if (n <= 4) return "warning.main";
  if (n <= 6) return "info.main";
  if (n <= 8) return "success.main";
  return "primary.main";
}

/**
 * Satisfaction dots (Option A) + color by bucket.
 * Still readable without color (filled vs outlined).
 */
function DotMeter({ value, size = 8, colorSx = "primary.main" }) {
  const n = Math.max(0, Math.min(10, Number(value) || 0));
  const filled = Math.round(n);

  return (
    <Box
      aria-hidden="true"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      {Array.from({ length: 10 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            width: size,
            height: size,
            borderRadius: "999px",
            border: "1px solid",
            bgcolor: i < filled ? colorSx : "transparent",
            borderColor: i < filled ? colorSx : "divider",
          }}
        />
      ))}
    </Box>
  );
}

function SatisfactionInline({
  metricKey,
  value,
  size = 8,
  emphasize = false,
  mode = "full", // "full" | "compact"
}) {
  const v = Number(value);
  const hasValue = value !== null && value !== undefined && !Number.isNaN(v);
  const qual = hasValue ? describe(metricKey, v) : "";
  const numericText = hasValue ? `${v}/10` : "—";
  const colorSx = hasValue ? scoreColorKey5(v) : "text.secondary";

  const tooltip = hasValue ? `${numericText}${qual ? ` · ${qual}` : ""}` : "No score";
  const showDots = mode === "full";

  return (
    <Tooltip title={tooltip}>
      <Box
        sx={{ display: "inline-flex", alignItems: "center", gap: 1, cursor: "default" }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {showDots ? <DotMeter value={hasValue ? v : 0} size={size} colorSx={colorSx} /> : null}

        <Typography
          component="span"
          variant={emphasize ? "subtitle2" : "body2"}
          sx={{
            fontWeight: emphasize ? 800 : 600,
            whiteSpace: "nowrap",
            color: colorSx,
          }}
        >
          {numericText}
          {qual ? (
            <Typography
              component="span"
              variant={emphasize ? "subtitle2" : "body2"}
              sx={{
                fontWeight: emphasize ? 800 : 600,
                color: colorSx,
              }}
            >
              {" "}
              · {qual}
            </Typography>
          ) : null}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function clamp2LinesSx(expanded) {
  return {
    whiteSpace: expanded ? "pre-wrap" : "normal",
    display: expanded ? "block" : "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: expanded ? "unset" : 2,
    overflow: "hidden",
  };
}

function getCustomTagsFromCustomerStory(customerStory) {
  const tags = Array.isArray(customerStory?.segmentation_data?.custom_tags)
    ? customerStory.segmentation_data.custom_tags
    : [];
  return tags
    .map((t) => String(t || "").trim())
    .filter((t) => t && t.toLowerCase() !== "unknown");
}

function getJtbdFromCustomerStory(customerStory) {
  const jtbd = Array.isArray(customerStory?.jobs_to_be_done) ? customerStory.jobs_to_be_done : [];
  return jtbd;
}

/**
 * Segmentation fallback:
 * Prefer Customer Story segmentation_data fields; if empty/unknown, fall back to meta.segmentation:
 *  - role_title -> job_title
 *  - company_name -> organization
 *  - company_size -> organization_size
 */
function segWithFallback({ customerStory, metaSeg }, key) {
  const storySeg = customerStory?.segmentation_data || {};
  const ms = metaSeg || {};

  const getStory = (k) => normalizeOptionalValue(storySeg?.[k]);

  if (key === "job_title") {
    const v = getStory("job_title");
    if (v !== "—") return v;
    return normalizeOptionalValue(ms.role_title || ms.job_title);
  }
  if (key === "organization") {
    const v = getStory("organization");
    if (v !== "—") return v;
    return normalizeOptionalValue(ms.company_name);
  }
  if (key === "organization_size") {
    const v = getStory("organization_size");
    if (v !== "—") return v;
    return normalizeOptionalValue(ms.company_size);
  }
  if (key === "persona") {
    return normalizeOptionalValue(storySeg?.persona);
  }
  if (key === "country") {
    return normalizeOptionalValue(storySeg?.country);
  }
  return "—";
}

function InterviewSummaryCard({
  detail,
  customerStory,
  avatarObjectUrl,
  avatarLoading,
  onBack,
}) {
  const [aboutExpanded, setAboutExpanded] = useState(false);

  const meta = detail?.meta || {};
  const metaSeg = meta?.segmentation || {};

  // Infer origin for the chip (keep using meta.product_name for this heuristic)
  const rawProductName = normalizeOptionalValue(meta?.product_name);
  const inferredOrigin =
    rawProductName.toLowerCase() === "self upload" || rawProductName.toLowerCase() === "self-upload"
      ? "self_upload"
      : "ai_interview";

  // New: topic (Product vs JTBD)
  const focusType = String(meta?.interview_focus_type || "product").toLowerCase();
  const topicName = normalizeOptionalValue(
    meta?.interview_topic_name || meta?.topic_name || meta?.product_name
  );
  const topicChipLabel =
    focusType === "jtbd" ? `Workflow: ${topicName}` : `Product: ${topicName}`;

  const typeLabel = originLabel(inferredOrigin);

  const name =
    normalizeOptionalValue(customerStory?.customer_name) !== "—"
      ? normalizeOptionalValue(customerStory?.customer_name)
      : normalizeOptionalValue(meta?.interviewee_name);

  const date =
    normalizeOptionalValue(customerStory?.conversation_date) !== "—"
      ? normalizeOptionalValue(customerStory?.conversation_date)
      : formatDateOnly(meta?.created_at);

  const about =
    normalizeOptionalValue(customerStory?.about_me) !== "—"
      ? normalizeOptionalValue(customerStory?.about_me)
      : "—";

  // Prefer Customer Story solution if present.
  // For JTBD interviews, do NOT fall back to the topic as the "solution".
  const solutionFromStory = normalizeOptionalValue(customerStory?.solution_choice_category);
  const solution =
    solutionFromStory !== "—"
      ? solutionFromStory
      : rawProductName.toLowerCase().includes("self upload")
        ? "—"
        : focusType === "jtbd"
          ? "—"
          : rawProductName;

  const satisfaction = customerStory?.satisfaction_level ?? meta?.satisfaction_level ?? null;

  const tags = getCustomTagsFromCustomerStory(customerStory);
  const jtbd = getJtbdFromCustomerStory(customerStory);

  const jobTitle = segWithFallback({ customerStory, metaSeg }, "job_title");
  const org = segWithFallback({ customerStory, metaSeg }, "organization");
  const orgSize = segWithFallback({ customerStory, metaSeg }, "organization_size");
  const persona = segWithFallback({ customerStory, metaSeg }, "persona");

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        mb: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
        {/* Left */}
        <Box sx={{ display: "flex", gap: 2, minWidth: 0, flex: 1 }}>
          <Box sx={{ position: "relative" }}>
            <Avatar
              src={
                avatarObjectUrl ||
                (detail?.avatar_url ? `${BACKEND_URL}${detail.avatar_url}?t=${Date.now()}` : "")
              }
              alt={name === "—" ? "Interviewee avatar" : `${name} avatar`}
              sx={{ width: 56, height: 56 }}
            />
            {avatarLoading ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={18} />
              </Box>
            ) : null}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            {/* Name + chips row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="h6" sx={{ lineHeight: 1.2, mr: 0.5 }}>
                {name}
              </Typography>

              <Chip size="small" variant="outlined" label={typeLabel} />
              <Chip size="small" variant="outlined" label={topicChipLabel} />
              <Chip size="small" variant="outlined" label={date} />

              {/* Tags (kept compact) */}
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                {tags.length === 0 ? (
                  <Chip size="small" variant="outlined" label="No tags" />
                ) : (
                  tags.slice(0, 3).map((t) => <Chip key={t} size="small" label={t} />)
                )}
                {tags.length > 3 ? <Chip size="small" variant="outlined" label={`+${tags.length - 3}`} /> : null}
              </Box>
            </Box>

            {/* About me */}
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
                About me
              </Typography>
              <Typography variant="body2" sx={clamp2LinesSx(aboutExpanded)}>
                {about}
              </Typography>

              {String(about).length > 240 ? (
                <Box sx={{ mt: 0.5 }}>
                  <Typography
                    component="button"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAboutExpanded((v) => !v);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      textDecoration: "underline",
                      font: "inherit",
                    }}
                    aria-expanded={aboutExpanded}
                  >
                    {aboutExpanded ? "Less" : "More"}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>

        {/* Right */}
        <Box sx={{ textAlign: "right", minWidth: { xs: 160, sm: 240 } }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Solution
          </Typography>
          <Typography
            variant="h6"
            sx={{
              lineHeight: 1.1,
              fontWeight: 900,
              maxWidth: 260,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              ml: "auto",
            }}
            title={solution === "—" ? "" : solution}
          >
            {solution}
          </Typography>

          <Box sx={{ mt: 0.75 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Satisfaction
            </Typography>
            <SatisfactionInline
              metricKey="satisfaction_level"
              value={satisfaction}
              size={7}
              emphasize
              mode="full"
            />
          </Box>

        </Box>
      </Box>

      {/* JTBD (compact rows, 1–2 typical) */}
      <Divider sx={{ my: 1.5 }} />
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
          Jobs to be done
        </Typography>

        {jtbd.length === 0 ? (
          <Typography variant="body2">—</Typography>
        ) : (
          <Stack spacing={1}>
            {jtbd.slice(0, 2).map((j, idx) => (
              <Box
                key={`${j?.job_to_be_done_category || "jtbd"}-${idx}`}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontWeight: 700, lineHeight: 1.3 }}>
                  {j?.job_to_be_done_category || "—"}
                </Typography>

                {/* Less distracting: NO dots for JTBD (compact mode) */}
                <SatisfactionInline
                  metricKey="satisfaction"
                  value={j?.satisfaction}
                  size={6}
                  emphasize={false}
                  mode="compact"
                />
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Segmentation footer row (spread across bottom) */}
      <Divider sx={{ my: 1.5 }} />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Job title
            </Typography>
            <Typography variant="body2">{jobTitle}</Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Organization
            </Typography>
            <Typography variant="body2">{org}</Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Org size
            </Typography>
            <Typography variant="body2">{orgSize}</Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
            Persona
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={isEmptyish(persona) ? "Persona: —" : `Persona: ${persona}`}
            onMouseDown={(e) => e.stopPropagation()}
          />
        </Box>
      </Box>
    </Paper>
  );
}

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

export function InterviewViewerPage() {
  const navigate = useNavigate();
  const { interviewId } = useParams();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Tabs under video: 0 = Customer Story, 1 = Transcript, 2 = Interview questions
  const [tab, setTab] = useState(0);

  // Refs for screen + webcam videos
  const screenVideoRef = useRef(null);
  const webcamVideoRef = useRef(null);

  // Container + overlay refs for dragging webcam
  const videoContainerRef = useRef(null);
  const webcamWrapperRef = useRef(null);

  // Draggable webcam position
  const [webcamPos, setWebcamPos] = useState({ x: 16, y: 16 });
  const [isDraggingWebcam, setIsDraggingWebcam] = useState(false);
  const dragOffsetRef = useRef({ offsetX: 0, offsetY: 0 });

  // Story preview state (section is hidden for now but leaving logic intact)
  const [story, setStory] = useState(null);
  const [storyLoading, setStoryLoading] = useState(false);
  const [storyError, setStoryError] = useState("");

  // Saved Customer Story JSON (not the preview)
  const [customerStory, setCustomerStory] = useState(null);
  const [customerStoryLoading, setCustomerStoryLoading] = useState(false);
  const [customerStoryError, setCustomerStoryError] = useState("");

  // Avatar preview (fetch as blob so it works even if endpoint needs auth)
  const [avatarObjectUrl, setAvatarObjectUrl] = useState("");
  const [avatarLoading, setAvatarLoading] = useState(false);

  // Review / analysis state
  const [reviewNotes, setReviewNotes] = useState("");
  const [savingReview, setSavingReview] = useState(false);
  const [reviewError, setReviewError] = useState("");

  // Unified video control state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  // True only while the duration-recovery seek is in flight (see
  // recoverInfiniteDuration), so the sync loop ignores the bogus playhead.
  const recoveringDurationRef = useRef(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);

  const token = useMemo(() => getToken(), []);
  const loggedIn = !!token;

  const adminUser = useMemo(() => isAdmin(), []);

  const hasVideo = Boolean(detail?.video?.screen_url || detail?.video?.webcam_url);

  const handleJumpToSeconds = useCallback(
    (seconds) => {
      if (!hasVideo) return;
      if (typeof seconds !== "number" || Number.isNaN(seconds)) return;

      // scroll to video
      if (videoContainerRef.current) {
        videoContainerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      // seek
      handleSeek(seconds);

      // autoplay
      const main = screenVideoRef.current;
      if (main) {
        try {
          main.muted = isMuted;
          main.playbackRate = playbackRate;
          main.play().catch(() => {});
          setIsPlaying(true);
        } catch {}
      }
    },
    [hasVideo, handleSeek, isMuted, playbackRate]
  );

  useEffect(() => {
    async function fetchDetail() {
      try {
        setLoading(true);
        setError("");
        const res = await fetch(`${BACKEND_URL}/api/interviews/${interviewId}/detail`, {
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${res.status}`);
        }
        const data = await res.json();
        setDetail(data);
      } catch (err) {
        console.error("Error fetching interview detail", err);
        setError(err.message || "Failed to load interview detail.");
      } finally {
        setLoading(false);
      }
    }

    if (interviewId && loggedIn) {
      fetchDetail();
    }
  }, [interviewId, loggedIn]);

  useEffect(() => {
    async function fetchCustomerStory() {
      if (!interviewId || !loggedIn) return;
      if (!detail?.meta) return;

      // Only try if meta says it exists
      if (!detail.meta.customer_story_filename) {
        setCustomerStory(null);
        setCustomerStoryError("");
        return;
      }

      try {
        setCustomerStoryLoading(true);
        setCustomerStoryError("");

        const res = await fetch(
          `${BACKEND_URL}/api/interviews/${encodeURIComponent(interviewId)}/customer-story`,
          {
            headers: {
              "Content-Type": "application/json",
              ...authHeaders(),
            },
          }
        );

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${res.status}`);
        }

        const data = await res.json();
        setCustomerStory(data.story || null);
      } catch (err) {
        console.error("Error fetching customer story JSON", err);
        setCustomerStory(null);
        setCustomerStoryError(err.message || "Failed to load customer story JSON.");
      } finally {
        setCustomerStoryLoading(false);
      }
    }

    fetchCustomerStory();
  }, [interviewId, loggedIn, detail]);

  // When meta first loads / changes, hydrate reviewNotes
  useEffect(() => {
    if (detail && detail.meta) {
      setReviewNotes(detail.meta.review_notes || "");
    }
  }, [detail]);

  // Load avatar image (if it exists)
  useEffect(() => {
    let alive = true;

    async function fetchAvatar() {
      if (!interviewId || !loggedIn) return;
      if (!detail?.meta) return;

      if (!detail.meta.avatar_filename) {
        setAvatarObjectUrl("");
        return;
      }

      setAvatarLoading(true);

      try {
        const res = await fetch(
          `${BACKEND_URL}/api/interviews/${encodeURIComponent(interviewId)}/avatar`,
          { headers: { ...authHeaders() } }
        );

        if (!res.ok) {
          if (alive) setAvatarObjectUrl("");
          return;
        }

        const blob = await res.blob();
        const url = URL.createObjectURL(blob);

        if (!alive) {
          URL.revokeObjectURL(url);
          return;
        }

        setAvatarObjectUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch (e) {
        console.warn("Failed to load avatar:", e);
        if (alive) setAvatarObjectUrl("");
      } finally {
        if (alive) setAvatarLoading(false);
      }
    }

    fetchAvatar();

    return () => {
      alive = false;
      setAvatarObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return "";
      });
    };
  }, [interviewId, loggedIn, detail]);

  // Auto-load Customer Story preview if available (kept for future use)
  useEffect(() => {
    if (!interviewId || !loggedIn) return;
    if (!detail?.meta) return;

    const hasStory =
      !!detail.meta.customer_story_filename ||
      detail.meta.analysis_status === "complete";

    if (!hasStory) return;
    if (story || storyLoading) return;

    handleLoadStory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId, loggedIn, detail, story, storyLoading]);

  // Global mouse move / up for dragging webcam overlay
  useEffect(() => {
    function handleMouseMove(e) {
      if (!isDraggingWebcam) return;
      if (!videoContainerRef.current) return;

      const containerRect = videoContainerRef.current.getBoundingClientRect();
      const wrapperRect =
        webcamWrapperRef.current?.getBoundingClientRect() || null;

      const rawX =
        e.clientX - containerRect.left - dragOffsetRef.current.offsetX;
      const rawY =
        e.clientY - containerRect.top - dragOffsetRef.current.offsetY;

      const maxX = wrapperRect
        ? containerRect.width - wrapperRect.width
        : containerRect.width - 100;
      const maxY = wrapperRect
        ? containerRect.height - wrapperRect.height
        : containerRect.height - 100;

      const newX = Math.max(0, Math.min(rawX, Math.max(maxX, 0)));
      const newY = Math.max(0, Math.min(rawY, Math.max(maxY, 0)));

      setWebcamPos({ x: newX, y: newY });
    }

    function handleMouseUp() {
      if (isDraggingWebcam) {
        setIsDraggingWebcam(false);
      }
    }

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDraggingWebcam]);

  function handleWebcamDragStart(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!webcamWrapperRef.current) return;

    const wrapperRect = webcamWrapperRef.current.getBoundingClientRect();
    dragOffsetRef.current = {
      offsetX: e.clientX - wrapperRect.left,
      offsetY: e.clientY - wrapperRect.top,
    };
    setIsDraggingWebcam(true);
  }

  // Keep screen + webcam in sync and drive shared controls
  function getVideos() {
    return [screenVideoRef.current, webcamVideoRef.current].filter(Boolean);
  }

  // Drag the playhead past the end so the browser reads to the last frame and
  // reports a real duration, then rewind. While it runs, currentTime is a
  // nonsense value, so the sync loop below ignores it — otherwise the webcam
  // would be dragged to the same nonsense position and the clock would flash it.
  function recoverInfiniteDuration(video) {
    if (recoveringDurationRef.current) return;
    recoveringDurationRef.current = true;

    const finish = (seconds) => {
      video.removeEventListener("durationchange", onDurationChange);
      clearTimeout(timer);
      try {
        video.currentTime = 0;
      } catch {}
      recoveringDurationRef.current = false;
      if (Number.isFinite(seconds)) {
        setDuration(seconds);
        setCurrentTime(0);
      }
    };

    const onDurationChange = () => {
      if (Number.isFinite(video.duration)) finish(video.duration);
    };

    // A file that never resolves must not strand the player in the seek state.
    const timer = setTimeout(() => finish(video.duration), 5000);

    video.addEventListener("durationchange", onDurationChange);
    try {
      // Large enough to land past the end of any interview recording; the
      // browser clamps to the real last frame rather than failing.
      video.currentTime = 1e7;
    } catch {
      finish(video.duration);
    }
  }

  function handleLoadedMetadata() {
    const mainVideo = screenVideoRef.current;
    if (mainVideo && Number.isFinite(mainVideo.duration)) {
      setDuration(mainVideo.duration);
    } else if (mainVideo) {
      // MediaRecorder writes WebM as a live stream, so the header carries no
      // duration and the browser reports Infinity — which reached the UI as
      // "Infinity:NaN" and left the scrubber with an infinite range. Seeking
      // past the end forces the browser to scan to the last frame and emit a
      // real duration, after which we rewind. Nothing else can recover it
      // client-side; the alternative is remuxing the file on upload.
      recoverInfiniteDuration(mainVideo);
    }

    const videos = getVideos();
    videos.forEach((v) => {
      try {
        v.muted = false;
        v.volume = 1;
        v.playbackRate = playbackRate;
      } catch {}
    });
    setIsMuted(false);
  }

  function handleTimeUpdate() {
    const mainVideo = screenVideoRef.current;
    if (!mainVideo) return;
    // Mid-recovery the playhead is parked past the end; that position is not
    // real playback and must not be published or synced to.
    if (recoveringDurationRef.current) return;

    const t = mainVideo.currentTime || 0;
    setCurrentTime(t);

    const webcam = webcamVideoRef.current;
    if (webcam) {
      const diff = Math.abs((webcam.currentTime || 0) - t);
      if (diff > 0.1) {
        try {
          webcam.currentTime = t;
        } catch {}
      }
    }
  }

  function handlePlayPause() {
    const videos = getVideos();
    if (videos.length === 0) return;

    if (isPlaying) {
      videos.forEach((v) => {
        try {
          v.pause();
        } catch {}
      });
      setIsPlaying(false);
    } else {
      videos.forEach((v) => {
        try {
          v.muted = isMuted;
          v.playbackRate = playbackRate;
          v.play().catch(() => {});
        } catch {}
      });
      setIsPlaying(true);
    }
  }

  function handleToggleMute() {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    const videos = getVideos();
    videos.forEach((v) => {
      try {
        v.muted = newMuted;
      } catch {}
    });
  }

  function handleProgressChange(e) {
    const value = parseFloat(e.target.value) || 0;
    setCurrentTime(value);

    const videos = getVideos();
    videos.forEach((v) => {
      try {
        v.currentTime = value;
      } catch {}
    });

    if (isPlaying) {
      videos.forEach((v) => {
        try {
          v.play().catch(() => {});
        } catch {}
      });
    }
  }

  function handlePlaybackRateChange(e) {
    const rate = parseFloat(e.target.value) || 1;
    setPlaybackRate(rate);
    const videos = getVideos();
    videos.forEach((v) => {
      try {
        v.playbackRate = rate;
      } catch {}
    });
  }

  function handleFullscreenToggle() {
    const container = videoContainerRef.current;
    if (!container) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else if (container.requestFullscreen) {
      container.requestFullscreen().catch(() => {});
    }
  }

  function handleDownload(which) {
    if (!detail || !detail.video) return;
    const screenUrl = detail.video.screen_url;
    const webcamUrl = detail.video.webcam_url;

    const url = which === "webcam" ? webcamUrl : screenUrl || webcamUrl;
    if (!url) return;

    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = which === "webcam" ? "webcam_master.webm" : "screen_master.webm";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }

  function handleSeek(seconds) {
    const target = typeof seconds === "number" ? seconds : 0;
    setCurrentTime(target);

    const videos = getVideos();
    videos.forEach((vid) => {
      try {
        vid.currentTime = target;
      } catch {}
    });

    if (isPlaying) {
      videos.forEach((v) => {
        try {
          v.play().catch(() => {});
        } catch {}
      });
    }
  }

  // Story preview (kept for future use)
  async function handleLoadStory() {
    if (!interviewId) return;
    setStoryError("");
    setStoryLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/interviews/${interviewId}/story-preview`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setStory(data.story || null);
    } catch (err) {
      console.error("Error fetching story preview", err);
      setStoryError(err.message || "Failed to generate story preview.");
    } finally {
      setStoryLoading(false);
    }
  }

  // Review / analysis status updates
  async function handleReviewUpdate({ status, forceReanalyze = false }) {
    if (!interviewId || !detail || !detail.meta) return;

    setSavingReview(true);
    setReviewError("");

    try {
      const res = await fetch(
        `${BACKEND_URL}/api/interviews/${encodeURIComponent(interviewId)}/review`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          body: JSON.stringify({
            review_status: status,
            review_notes: reviewNotes,
            force_reanalyze: forceReanalyze,
          }),
        }
      );

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      setDetail((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          meta: {
            ...(prev.meta || {}),
            review_status: body.review_status,
            review_notes: body.review_notes,
            analysis_status: body.analysis_status,
            analysis_error: body.analysis_error,
            customer_story_filename: body.customer_story_filename,
          },
        };
      });

      setReviewNotes(body.review_notes || "");
    } catch (err) {
      console.error("Error updating review", err);
      setReviewError(err.message || "Failed to update review.");
    } finally {
      setSavingReview(false);
    }
  }

  const handleBack = useCallback(() => navigate("/interviews"), [navigate]);

  // ------------------ RENDER ------------------
  return (
    <ThemeProvider theme={theme}>
      <NavBar />

      <Box sx={{ p: 2, maxWidth: 1000, mx: "auto" }}>
        {!loggedIn ? (
          <Paper sx={{ p: 2 }}>
            <Alert severity="warning" sx={{ mb: 1 }}>
              You need to be logged in to view this interview.
            </Alert>
            <Button variant="outlined" onClick={() => navigate("/")}>
              Go to Home
            </Button>
          </Paper>
        ) : loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={22} />
            <Typography>Loading interview…</Typography>
          </Box>
        ) : error ? (
          <Paper sx={{ p: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button variant="outlined" component={Link} to="/interviews">
              Back to interviews
            </Button>
          </Paper>
        ) : !detail ? (
          <Paper sx={{ p: 2 }}>
            <Typography>No data found for this interview.</Typography>
            <Button variant="outlined" component={Link} to="/interviews" sx={{ mt: 1 }}>
              Back to interviews
            </Button>
          </Paper>
        ) : (
          <>
            {/* Top: summary card (replaces AI Interview information box) */}
            <InterviewSummaryCard
              detail={detail}
              customerStory={customerStory}
              avatarObjectUrl={avatarObjectUrl}
              avatarLoading={avatarLoading}
              onBack={handleBack}
            />

            {/* VIDEO SECTION (comes next) */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                Video
              </Typography>

              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                {detail.video?.screen_url || detail.video?.webcam_url ? (
                  <>
                    <div
                      ref={videoContainerRef}
                      style={{
                        position: "relative",
                        width: "100%",
                        maxHeight: "480px",
                        background: "#000",
                        overflow: "hidden",
                        borderRadius: "8px",
                      }}
                    >
                      {/* Main video: screen if available, otherwise webcam */}
                      <video
                        ref={screenVideoRef}
                        src={detail.video.screen_url || detail.video.webcam_url}
                        controls={false}
                        onLoadedMetadata={handleLoadedMetadata}
                        onTimeUpdate={handleTimeUpdate}
                        onEnded={() => setIsPlaying(false)}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handlePlayPause();
                        }}
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "block",
                          background: "#000",
                          cursor: "pointer", // ✅ indicates click-to-toggle
                        }}
                      />

                      {/* Webcam PiP overlay */}
                      {detail.video.screen_url && detail.video.webcam_url && (
                        <div
                          ref={webcamWrapperRef}
                          onMouseDown={handleWebcamDragStart}
                          style={{
                            position: "absolute",
                            top: webcamPos.y,
                            left: webcamPos.x,
                            width: "24%",
                            minWidth: "150px",
                            maxWidth: "220px",
                            cursor: "move",
                            boxShadow: "0 0 8px rgba(0,0,0,0.7)",
                            borderRadius: "6px",
                            overflow: "hidden",
                            border: "2px solid #fff",
                          }}
                        >
                          <video
                            ref={webcamVideoRef}
                            src={detail.video.webcam_url}
                            controls={false}
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "block",
                              background: "#000",
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              fontSize: "0.7rem",
                              background: "rgba(0,0,0,0.6)",
                              color: "#fff",
                              padding: "2px 6px",
                              borderRadius: "999px",
                            }}
                          >
                            Drag
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Unified custom controls (kept intact) */}
                    <div
                      style={{
                        marginTop: "0.75rem",
                        padding: "0.5rem 0.75rem",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                        background: "#fafafa",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                      }}
                    >
                      <input
                        type="range"
                        min={0}
                        max={Number.isFinite(duration) ? duration : 0}
                        step="0.1"
                        value={currentTime}
                        onChange={handleProgressChange}
                        style={{ width: "100%" }}
                      />
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: "0.5rem",
                        }}
                      >
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                          <button type="button" onClick={handlePlayPause}>
                            {isPlaying ? "Pause" : "Play"}
                          </button>
                          <button type="button" onClick={handleToggleMute}>
                            {isMuted ? "Unmute" : "Mute"}
                          </button>
                          <button type="button" onClick={handleFullscreenToggle}>
                            Full screen
                          </button>
                        </div>

                        <div
                          style={{
                            fontSize: "0.85rem",
                            fontFamily: "monospace",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatSeconds(currentTime)} / {formatSeconds(duration)}
                        </div>

                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                          <label style={{ fontSize: "0.85rem" }}>
                            Speed:&nbsp;
                            <select value={playbackRate} onChange={handlePlaybackRateChange}>
                              <option value="0.5">0.5x</option>
                              <option value="0.75">0.75x</option>
                              <option value="1">1x</option>
                              <option value="1.25">1.25x</option>
                              <option value="1.5">1.5x</option>
                              <option value="2">2x</option>
                            </select>
                          </label>

                          <div style={{ display: "flex", gap: "0.25rem" }}>
                            {detail.video?.screen_url && (
                              <button type="button" onClick={() => handleDownload("screen")}>
                                Download screen
                              </button>
                            )}
                            {detail.video?.webcam_url && (
                              <button type="button" onClick={() => handleDownload("webcam")}>
                                Download webcam
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {detail.video.screen_url && detail.video.webcam_url && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        The main player shows the <strong>screen recording</strong>. The{" "}
                        <strong>webcam</strong> appears as a draggable picture-in-picture overlay.
                        Clicking any timestamp keeps both videos in sync.
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography color="text.secondary">No video available for this interview.</Typography>
                )}
              </Paper>
            </Box>

            {/* Tabs under video */}
            <Box sx={{ mt: 2 }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                aria-label="Interview content tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Customer Story" />
                <Tab label="Transcript" />
                <Tab label="Interview questions" />
              </Tabs>

              {/* CUSTOMER STORY TAB */}
              <TabPanel value={tab} index={0}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  {customerStoryError && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      {customerStoryError}
                    </Alert>
                  )}

                  {customerStoryLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={18} />
                      <Typography>Loading customer story…</Typography>
                    </Box>
                  ) : !customerStory ? (
                    <Typography sx={{ color: "text.secondary" }}>
                      No Customer Story JSON found for this interview.
                    </Typography>
                  ) : (
                    <CustomerStorySection
                      story={customerStory}
                      hasVideo={hasVideo}
                      onJumpToSeconds={handleJumpToSeconds}
                    />
                  )}
                </Paper>

                {/* Interview details (collapsed) — keeps data accessible without stealing space */}
                <Accordion sx={{ mt: 2 }} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Interview details</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Interviewee name
                        </Typography>
                        <Typography variant="body2">{detail.meta?.interviewee_name || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Product name
                        </Typography>
                        <Typography variant="body2">{detail.meta?.product_name || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Created at (date)
                        </Typography>
                        <Typography variant="body2">{formatDateOnly(detail.meta?.created_at)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Industry
                        </Typography>
                        <Typography variant="body2">{detail.meta?.industry || "—"}</Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Meta segmentation
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">company_name</Typography>
                        <Typography variant="body2">{detail.meta?.segmentation?.company_name || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">company_size</Typography>
                        <Typography variant="body2">{detail.meta?.segmentation?.company_size || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">job_title</Typography>
                        <Typography variant="body2">
                          {detail.meta?.segmentation?.job_title ||
                            detail.meta?.segmentation?.role_title ||
                            "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">decision_involvement</Typography>
                        <Typography variant="body2">{detail.meta?.segmentation?.decision_involvement || "—"}</Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Payment
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">payment_method</Typography>
                        <Typography variant="body2">{paymentMethodLabel(detail.meta?.payment?.method)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {paymentContactLabel(detail.meta?.payment?.method)}
                        </Typography>
                        <Typography variant="body2">
                          {normalizeOptionalValue(detail.meta?.payment?.email)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">payout_amount</Typography>
                        <Typography variant="body2">
                          {detail.meta?.payout_amount != null
                            ? `$${detail.meta.payout_amount}`
                            : "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">interviewee_email</Typography>
                        <Typography variant="body2">
                          {normalizeOptionalValue(detail.meta?.interviewee_email)}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* Diagnostics (debug-only) — collapsed */}
                <Accordion sx={{ mt: 1 }} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Diagnostics</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">interview_id</Typography>
                        <Typography variant="body2">{detail.meta?.interview_id || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">overall_status</Typography>
                        <Typography variant="body2">{detail.meta?.overall_status || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">video_status</Typography>
                        <Typography variant="body2">{detail.meta?.video_status || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">transcript_status</Typography>
                        <Typography variant="body2">{detail.meta?.transcript_status || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">review_status</Typography>
                        <Typography variant="body2">{detail.meta?.review_status || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">analysis_status</Typography>
                        <Typography variant="body2">{detail.meta?.analysis_status || "—"}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">analysis_completed_at (date)</Typography>
                        <Typography variant="body2">{formatDateOnly(detail.meta?.analysis_completed_at)}</Typography>
                      </Box>
                    </Box>

                    {/* Keep story preview logic accessible (not shown elsewhere yet) */}
                    {storyError ? (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Story preview error: {storyError}
                      </Alert>
                    ) : null}
                    {storyLoading ? (
                      <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={18} />
                        <Typography>Generating story preview…</Typography>
                      </Box>
                    ) : null}
                  </AccordionDetails>
                </Accordion>

                {/* Admin-only Review & Analysis */}
                {adminUser && (
                  <Paper
                    sx={{
                      p: 2,
                      mt: 2,
                      background: "#fafafa",
                      border: "1px solid #ddd",
                      borderRadius: 2,
                      position: "relative",
                    }}
                  >
                    <Chip
                      size="small"
                      label="Only visible to admins"
                      variant="outlined"
                      sx={{ position: "absolute", top: 12, right: 12 }}
                    />

                    <Typography variant="h6" sx={{ mt: 0 }}>
                      Review &amp; Analysis
                    </Typography>

                    {reviewError && (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        {reviewError}
                      </Alert>
                    )}

                    <div style={{ marginBottom: "0.25rem", fontSize: "0.9rem" }}>
                      <strong>Review status:</strong>{" "}
                      {detail.meta.review_status || "pending"}
                    </div>
                    <div style={{ marginBottom: "0.25rem", fontSize: "0.9rem" }}>
                      <strong>Analysis status:</strong>{" "}
                      {detail.meta.analysis_status || "not_started"}
                    </div>
                    {detail.meta.analysis_error && (
                      <div style={{ marginBottom: "0.25rem", fontSize: "0.85rem", color: "red" }}>
                        <strong>Analysis error:</strong> {detail.meta.analysis_error}
                      </div>
                    )}
                    {detail.meta.customer_story_filename && (
                      <div style={{ marginBottom: "0.25rem", fontSize: "0.85rem" }}>
                        <strong>Customer story file:</strong>{" "}
                        <code>{detail.meta.customer_story_filename}</code>
                      </div>
                    )}

                    <div style={{ margin: "0.5rem 0" }}>
                      <label>
                        Review notes:{" "}
                        <textarea
                          value={reviewNotes}
                          onChange={(e) => setReviewNotes(e.target.value)}
                          rows={3}
                          style={{ width: "100%" }}
                        />
                      </label>
                    </div>

                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        disabled={savingReview}
                        onClick={() => handleReviewUpdate({ status: "approved" })}
                      >
                        Approve &amp; Start Analysis
                      </button>

                      <button
                        type="button"
                        disabled={savingReview}
                        onClick={() => handleReviewUpdate({ status: "rejected" })}
                      >
                        Reject Interview
                      </button>

                      {(detail.meta.analysis_status === "complete" ||
                        detail.meta.analysis_status === "error") && (
                        <button
                          type="button"
                          disabled={savingReview}
                          onClick={() =>
                            handleReviewUpdate({
                              status: "approved",
                              forceReanalyze: true,
                            })
                          }
                        >
                          Re-run Analysis
                        </button>
                      )}
                    </div>
                  </Paper>
                )}
              </TabPanel>

              {/* TRANSCRIPT TAB */}
              <TabPanel value={tab} index={1}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  {((detail.transcript && detail.transcript.messages) || []).length === 0 ? (
                    <Typography>No transcript messages yet.</Typography>
                  ) : (
                    <div>
                      {detail.transcript.messages.map((msg, idx) => {
                        if (msg.role === "assistant") {
                          const t = msg.timestamp;
                          const control = msg.control || {};
                          const coreId = msg.core_question_id;

                          // Build core map on the fly from structure
                          const coreMap = {};
                          const phasesStruct = (detail.structure && detail.structure.phases) || [];
                          phasesStruct.forEach((p) => {
                            const phaseLabel = p.label || p.id;
                            (p.core_questions || []).forEach((cq) => {
                              if (!cq.id) return;
                              coreMap[cq.id] = { phaseLabel, corePrompt: cq.prompt || "" };
                            });
                          });
                          const coreMeta = coreId ? coreMap[coreId] : null;

                          return (
                            <div
                              key={idx}
                              style={{
                                marginBottom: "0.75rem",
                                padding: "0.75rem",
                                borderLeft: "4px solid #4a90e2",
                                background: "#f0f6ff",
                                borderRadius: "8px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "0.85rem",
                                  color: "#555",
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: "0.5rem",
                                }}
                              >
                                <span>
                                  <strong>AI</strong>
                                </span>
                                {t != null && (
                                  <span>
                                    ·{" "}
                                    <button
                                      type="button"
                                      onClick={() => handleSeek(t)}
                                      style={{
                                        border: "none",
                                        padding: 0,
                                        margin: 0,
                                        background: "none",
                                        color: "#0070f3",
                                        cursor: "pointer",
                                      }}
                                    >
                                      {formatSeconds(t)}
                                    </button>
                                  </span>
                                )}
                                {coreMeta && (
                                  <span
                                    style={{
                                      fontSize: "0.75rem",
                                      background: "#e1ecff",
                                      padding: "0.1rem 0.5rem",
                                      borderRadius: "999px",
                                    }}
                                  >
                                    {coreMeta.phaseLabel} · Core
                                  </span>
                                )}
                              </div>
                              <div style={{ marginTop: "0.35rem" }}>{msg.text}</div>
                              {coreMeta?.corePrompt && (
                                <div
                                  style={{
                                    marginTop: "0.35rem",
                                    fontSize: "0.8rem",
                                    color: "#555",
                                  }}
                                >
                                  <strong>Core question:</strong> {coreMeta.corePrompt}
                                </div>
                              )}
                              {control &&
                                (control.core_question_complete != null ||
                                  control.phase_complete != null ||
                                  control.notes) && (
                                  <div
                                    style={{
                                      marginTop: "0.35rem",
                                      fontSize: "0.8rem",
                                      color: "#444",
                                      borderTop: "1px dashed #ccc",
                                      paddingTop: "0.35rem",
                                    }}
                                  >
                                    <strong>Control:</strong>{" "}
                                    {control.core_question_complete != null && (
                                      <span>
                                        core_question_complete:{" "}
                                        {control.core_question_complete ? "true" : "false"};{" "}
                                      </span>
                                    )}
                                    {control.phase_complete != null && (
                                      <span>
                                        phase_complete: {control.phase_complete ? "true" : "false"};{" "}
                                      </span>
                                    )}
                                    {control.notes && <span>notes: {control.notes}</span>}
                                  </div>
                                )}
                            </div>
                          );
                        }

                        // Interviewee message
                        const sentences = msg.sentences || [];
                        return (
                          <div
                            key={idx}
                            style={{
                              marginBottom: "0.75rem",
                              padding: "0.75rem",
                              borderLeft: "4px solid #7ed321",
                              background: "#f6fff4",
                              borderRadius: "8px",
                            }}
                          >
                            <div style={{ fontSize: "0.85rem", color: "#555" }}>
                              <strong>Interviewee</strong>
                            </div>
                            {sentences.length === 0 ? (
                              <div>(No text)</div>
                            ) : (
                              <div style={{ lineHeight: 1.6 }}>
                                {sentences.map((s, sIdx) => (
                                  <div
                                    key={sIdx}
                                    style={{ marginBottom: "0.25rem", cursor: "pointer" }}
                                    onClick={() => handleSeek(s.start)}
                                    title={
                                      s.start != null
                                        ? `Jump to ${formatSeconds(s.start)}`
                                        : undefined
                                    }
                                  >
                                    <span
                                      style={{
                                        fontFamily: "monospace",
                                        fontSize: "0.8rem",
                                        color: "#888",
                                        marginRight: "0.5rem",
                                      }}
                                    >
                                      [{formatSeconds(s.start)}
                                      {s.end != null && s.end !== s.start
                                        ? `–${formatSeconds(s.end)}`
                                        : ""}
                                      ]
                                    </span>
                                    <span>{s.text}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Paper>
              </TabPanel>

              {/* INTERVIEW QUESTIONS TAB */}
              <TabPanel value={tab} index={2}>


                {(detail.structure?.phases || []).length > 0 ? (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mt: 0 }}>
                      Interview questions
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      This summarizes how the AI moved through the interview guide. Click a timestamp to jump the video.
                    </Typography>

                    {detail.structure.phases.map((phase) => (
                      <div
                        key={phase.id}
                        style={{
                          marginBottom: "0.75rem",
                          paddingBottom: "0.75rem",
                          borderBottom: "1px dotted #ccc",
                        }}
                      >
                        <div style={{ fontWeight: "bold" }}>{phase.label || phase.id}</div>
                        {phase.learning_goal && (
                          <div
                            style={{
                              fontSize: "0.85rem",
                              color: "#555",
                              marginBottom: "0.25rem",
                            }}
                          >
                            Goal: {phase.learning_goal}
                          </div>
                        )}
                        {(phase.core_questions || []).map((cq) => (
                          <div
                            key={cq.id}
                            style={{
                              fontSize: "0.9rem",
                              marginLeft: "0.75rem",
                              marginTop: "0.35rem",
                            }}
                          >
                            <div>
                              <strong>Key question:</strong> {cq.prompt}
                            </div>
                            <div style={{ fontSize: "0.8rem", color: "#555" }}>
                              Follow up questions: {cq.num_questions}
                              {cq.first_timestamp != null && (
                                <>
                                  {" · Starts at "}
                                  <button
                                    type="button"
                                    onClick={() => handleSeek(cq.first_timestamp)}
                                    style={{
                                      border: "none",
                                      padding: 0,
                                      margin: 0,
                                      background: "none",
                                      color: "#0070f3",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {formatSeconds(cq.first_timestamp)}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </Paper>
                ) : (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography color="text.secondary">No interview questions available.</Typography>
                  </Paper>
                )}
              </TabPanel>
            </Box>
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default InterviewViewerPage;
